import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
  makeStyles,
} from '@material-ui/core'
import EmojiEventsOutlinedIcon from '@material-ui/icons/EmojiEventsOutlined'
import gameResultSectionStyles from './gameResultSection.styles'
import ScorePairInputs from './ScorePairInputs'
import SectionFieldset from '../SectionFieldset'
import ButtonWithLoader from '../../ui/ButtonWithLoader/ButtonWithLoader'
import TextButton from '../../ui/TextButton/TextButton'
import InlineConfirmation from './InlineConfirmation'
import MySnackbar from '../../MySnackbar/MySnackbar'
import STATS_GAME_STATUS from '../../../utils/constants/statsGameStatus'
import FORFEIT_REASONS from '../../../utils/constants/forfeitReasons'
import COLORS from '../../../utils/constants/colors'
import QueryKeys from '../../../utils/constants/queryKeys'
import getGameResult from '../../../Apis/stats/getGameResult'
import postGameScore from '../../../Apis/stats/postGameScore'
import postGameForfeit from '../../../Apis/stats/postGameForfeit'
import USER_PERMISSIONS from '../../../utils/constants/userPermissions'
import IsPermitted from '../../IsPermitted/IsPermitted'

const useStyles = makeStyles(gameResultSectionStyles)

const PANEL = { NONE: 'none', SCORE: 'score', FORFEIT: 'forfeit' }
// One confirmation for score/forfeit; two (REPLACE_1 → REPLACE_2) when recorded stats get replaced.
const CONFIRM = {
  NONE: 'none',
  SCORE: 'score',
  REPLACE_1: 'replace1',
  REPLACE_2: 'replace2',
  FORFEIT: 'forfeit',
}

const isValidPoints = (value) => /^\d+$/.test(String(value ?? '').trim())

const GameResultSection = ({ orgId, gameId, eventTeams, onResultSaved }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const queryClient = useQueryClient()

  const [homeTeam, awayTeam] = useMemo(() => {
    const teams = (eventTeams ?? []).filter((t) => t?.id)
    const home = teams.find((t) => t.isHomeTeam) ?? teams[0]
    const away = teams.find((t) => t !== home)
    return [home, away]
  }, [eventTeams])

  const [panel, setPanel] = useState(PANEL.NONE)
  const [confirm, setConfirm] = useState(CONFIRM.NONE)
  const [scoreValues, setScoreValues] = useState({})
  const [keepExistingStats, setKeepExistingStats] = useState(true)
  const [forfeitingTeamId, setForfeitingTeamId] = useState('')
  const [reason, setReason] = useState(FORFEIT_REASONS.STANDARD)
  const [formError, setFormError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const resultQuery = useQuery(
    [QueryKeys.GET_GAME_RESULT, gameId],
    () => getGameResult({ orgId, gameId }),
    { enabled: Boolean(orgId && gameId) },
  )
  const result = resultQuery.data
  const hasRecordedStats = Boolean(result?.hasRecordedStats)

  const onSaved = () => {
    queryClient.invalidateQueries([QueryKeys.GET_GAME_RESULT, gameId])
    queryClient.invalidateQueries([QueryKeys.GET_STATS_GAME_SETUP, gameId])
    queryClient.invalidateQueries(QueryKeys.GET_EVENT)
    queryClient.invalidateQueries(QueryKeys.GET_EVENTS)
    queryClient.invalidateQueries(QueryKeys.GET_GAMES_BY_CONTEXT)
    queryClient.invalidateQueries(QueryKeys.GET_STAGES)
    setConfirm(CONFIRM.NONE)
    setPanel(PANEL.NONE)
    setScoreValues({})
    setKeepExistingStats(true)
    setSnackbarOpen(true)
    if (onResultSaved) onResultSaved()
  }
  const onFailed = () => setConfirm(CONFIRM.NONE)

  const scoreMutation = useMutation(postGameScore, { onSuccess: onSaved, onError: onFailed })
  const forfeitMutation = useMutation(postGameForfeit, { onSuccess: onSaved, onError: onFailed })
  const isSaving = scoreMutation.isLoading || forfeitMutation.isLoading
  const isConfirming = confirm !== CONFIRM.NONE
  const isLocked = isSaving || isConfirming

  const scoresPayload = () => [
    { eventTeamId: homeTeam.id, points: Number(scoreValues[homeTeam.id]) },
    { eventTeamId: awayTeam.id, points: Number(scoreValues[awayTeam.id]) },
  ]
  const scoreLabel = () => `${scoreValues[homeTeam.id]}-${scoreValues[awayTeam.id]}`
  const bothScoresFilled = () =>
    isValidPoints(scoreValues[homeTeam?.id]) && isValidPoints(scoreValues[awayTeam?.id])
  const bothScoresEmpty = () =>
    String(scoreValues[homeTeam?.id] ?? '') === '' && String(scoreValues[awayTeam?.id] ?? '') === ''

  const currentScores = () =>
    Object.fromEntries((result?.scores ?? []).map((s) => [s.eventTeamId, String(s.points)]))

  const openPanel = (next) => {
    setFormError(null)
    setScoreValues(currentScores())
    setKeepExistingStats(true)
    setForfeitingTeamId('')
    setReason(FORFEIT_REASONS.STANDARD)
    setPanel(next)
  }

  const submitScore = () => {
    if (!bothScoresFilled()) {
      setFormError(translate('ra.gameDrawer.result.invalidScore'))
      return
    }
    setFormError(null)
    setConfirm(!keepExistingStats && hasRecordedStats ? CONFIRM.REPLACE_1 : CONFIRM.SCORE)
  }

  const runScoreMutation = () =>
    scoreMutation.mutate({
      orgId,
      gameId,
      scores: scoresPayload(),
      keepExistingStats: hasRecordedStats ? keepExistingStats : true,
    })

  const showForfeitScore = reason === FORFEIT_REASONS.LESS_THAN_2_PLAYERS && !hasRecordedStats

  const submitForfeit = () => {
    if (!forfeitingTeamId) {
      setFormError(translate('ra.gameDrawer.result.selectTeam'))
      return
    }
    if (showForfeitScore && !bothScoresFilled() && !bothScoresEmpty()) {
      setFormError(translate('ra.gameDrawer.result.bothScoresOrNone'))
      return
    }
    setFormError(null)
    setConfirm(CONFIRM.FORFEIT)
  }

  const runForfeitMutation = () =>
    forfeitMutation.mutate({
      orgId,
      gameId,
      forfeitingEventTeamId: forfeitingTeamId,
      reason,
      scores: showForfeitScore && bothScoresFilled() ? scoresPayload() : undefined,
    })

  const reasonLabel = (value) =>
    translate(
      value === FORFEIT_REASONS.LESS_THAN_2_PLAYERS
        ? 'ra.gameDrawer.result.reasonLessThan2Players'
        : 'ra.gameDrawer.result.reasonStandard',
    )
  const teamName = (id) => [homeTeam, awayTeam].find((t) => t?.id === id)?.name ?? ''

  const currentResultText = () => {
    if (!result || !homeTeam || !awayTeam) return translate('ra.gameDrawer.result.noResult')
    const points = (id) => result.scores?.find((s) => s.eventTeamId === id)?.points
    const score = result.scores?.length
      ? `${points(homeTeam.id) ?? 0}-${points(awayTeam.id) ?? 0}`
      : null
    if (result.status === STATS_GAME_STATUS.FORFEIT)
      return (
        translate('ra.gameDrawer.result.forfeitBy', {
          team: teamName(result.forfeitingEventTeamId),
          reason: reasonLabel(result.forfeitReason),
        }) + (score ? ` ${score}` : '')
      )
    if (result.status === STATS_GAME_STATUS.LIVE && score)
      return translate('ra.gameDrawer.result.live', { score })
    if (result.status === STATS_GAME_STATUS.COMPLETED && score)
      return translate('ra.gameDrawer.result.final', { score })
    return translate('ra.gameDrawer.result.noResult')
  }

  const confirmProps = {
    [CONFIRM.SCORE]: {
      title: translate('ra.gameDrawer.result.confirmScoreTitle'),
      text: translate('ra.gameDrawer.result.confirmScoreText', { score: scoreLabel() }),
      confirmLabel: translate('ra.gameDrawer.result.confirm'),
      onConfirm: runScoreMutation,
    },
    [CONFIRM.REPLACE_1]: {
      title: translate('ra.gameDrawer.result.confirmReplaceTitle'),
      text: translate('ra.gameDrawer.result.confirmReplaceText', { score: scoreLabel() }),
      confirmLabel: translate('ra.gameDrawer.result.continue'),
      onConfirm: () => setConfirm(CONFIRM.REPLACE_2),
      danger: true,
    },
    [CONFIRM.REPLACE_2]: {
      title: translate('ra.gameDrawer.result.confirmReplaceAgainTitle'),
      text: translate('ra.gameDrawer.result.confirmReplaceAgainText'),
      confirmLabel: translate('ra.gameDrawer.result.deleteAndReplace'),
      onConfirm: runScoreMutation,
      danger: true,
    },
    [CONFIRM.FORFEIT]: {
      title: translate('ra.gameDrawer.result.confirmForfeitTitle'),
      text: translate('ra.gameDrawer.result.confirmForfeitText', {
        team: teamName(forfeitingTeamId),
        reason: reasonLabel(reason),
      }),
      confirmLabel: translate('ra.gameDrawer.result.recordForfeit'),
      onConfirm: runForfeitMutation,
      danger: true,
    },
  }

  if (!homeTeam || !awayTeam) return null

  const cancelButton = (
    <TextButton
      type='button'
      label={translate('ra.buttons.cancel')}
      onClick={() => setPanel(PANEL.NONE)}
      disabled={isSaving}
    />
  )
  const confirmation = isConfirming && (
    <InlineConfirmation
      {...confirmProps[confirm]}
      cancelLabel={translate('ra.buttons.cancel')}
      onCancel={() => setConfirm(CONFIRM.NONE)}
      isLoading={isSaving}
    />
  )

  return (
    <IsPermitted permission={USER_PERMISSIONS.UPDATE_EVENT}>
      <SectionFieldset label={translate('ra.gameDrawer.result.section')}>
        <div className={classes.card}>
          <div className={classes.headerRow}>
            <EmojiEventsOutlinedIcon className={classes.headerIcon} />
            <Typography className={classes.resultText}>{currentResultText()}</Typography>
          </div>

          {panel === PANEL.NONE && (
            <div className={classes.actions}>
              <ButtonWithLoader
                type='button'
                label={translate('ra.gameDrawer.result.setScore')}
                onClick={() => openPanel(PANEL.SCORE)}
                disabled={resultQuery.isLoading}
              />
              <TextButton
                type='button'
                style={{ color: COLORS.red }}
                label={translate('ra.gameDrawer.result.forfeit')}
                onClick={() => openPanel(PANEL.FORFEIT)}
                disabled={resultQuery.isLoading}
              />
            </div>
          )}

          {panel === PANEL.SCORE && (
            <div className={classes.panel}>
              <ScorePairInputs
                homeTeam={homeTeam}
                awayTeam={awayTeam}
                values={scoreValues}
                onChange={setScoreValues}
                disabled={isLocked}
              />
              {hasRecordedStats && (
                <div className={classes.switchRow}>
                  <div className={classes.switchText}>
                    <Typography className={classes.resultText}>
                      {translate('ra.gameDrawer.result.keepStats')}
                    </Typography>
                    <Typography className={classes.hint}>
                      {translate('ra.gameDrawer.result.keepStatsDesc')}
                    </Typography>
                  </div>
                  <Switch
                    color='primary'
                    checked={keepExistingStats}
                    disabled={isLocked}
                    onChange={() => setKeepExistingStats((v) => !v)}
                    inputProps={{ 'aria-label': translate('ra.gameDrawer.result.keepStats') }}
                  />
                </div>
              )}
              {formError && <Typography className={classes.error}>{formError}</Typography>}
              {confirmation || (
                <div className={classes.panelActions}>
                  {cancelButton}
                  <ButtonWithLoader
                    type='button'
                    label={translate('ra.gameDrawer.result.saveScore')}
                    onClick={submitScore}
                  />
                </div>
              )}
            </div>
          )}

          {panel === PANEL.FORFEIT && (
            <div className={classes.panel}>
              <Typography className={classes.hint}>
                {translate('ra.gameDrawer.result.forfeitingTeam')}
              </Typography>
              <RadioGroup
                row
                value={forfeitingTeamId}
                onChange={(e) => setForfeitingTeamId(e.target.value)}
              >
                {[homeTeam, awayTeam].map((team) => (
                  <FormControlLabel
                    key={team.id}
                    value={team.id}
                    control={<Radio color='primary' />}
                    label={team.name}
                    disabled={isLocked}
                  />
                ))}
              </RadioGroup>
              <Typography className={classes.hint}>
                {translate('ra.gameDrawer.result.reason')}
              </Typography>
              <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
                <FormControlLabel
                  value={FORFEIT_REASONS.STANDARD}
                  control={<Radio color='primary' />}
                  label={translate('ra.gameDrawer.result.reasonStandard')}
                  disabled={isLocked}
                />
                <FormControlLabel
                  value={FORFEIT_REASONS.LESS_THAN_2_PLAYERS}
                  control={<Radio color='primary' />}
                  label={translate('ra.gameDrawer.result.reasonLessThan2Players')}
                  disabled={isLocked}
                />
              </RadioGroup>
              {showForfeitScore && (
                <>
                  <Typography className={classes.hint}>
                    {translate('ra.gameDrawer.result.scoreAtStop')}
                  </Typography>
                  <ScorePairInputs
                    homeTeam={homeTeam}
                    awayTeam={awayTeam}
                    values={scoreValues}
                    onChange={setScoreValues}
                    disabled={isLocked}
                  />
                  <Typography className={classes.hint}>
                    {translate('ra.gameDrawer.result.scoreAtStopHint')}
                  </Typography>
                </>
              )}
              {formError && <Typography className={classes.error}>{formError}</Typography>}
              {confirmation || (
                <div className={classes.panelActions}>
                  {cancelButton}
                  <ButtonWithLoader
                    type='button'
                    label={translate('ra.gameDrawer.result.recordForfeit')}
                    onClick={submitForfeit}
                    style={{ backgroundColor: COLORS.red }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <MySnackbar
          isOpen={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          isSuccess
          successMessage={translate('ra.gameDrawer.result.saved')}
        />
      </SectionFieldset>
    </IsPermitted>
  )
}

GameResultSection.propTypes = {
  orgId: PropTypes.string,
  gameId: PropTypes.string,
  eventTeams: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, isHomeTeam: PropTypes.bool }),
  ),
  teamIds: PropTypes.arrayOf(PropTypes.string),
  onResultSaved: PropTypes.func,
}

export default GameResultSection
